/**
 * /mic-test — Standalone microphone diagnostic page.
 *
 * Purpose: isolate whether "records nothing" is (a) a macOS / Chrome mic
 * permission or Continuity-Camera issue, (b) a MediaRecorder codec issue,
 * or (c) a HustleDictate extension issue. This page uses *exactly* the
 * same getUserMedia + MediaRecorder configuration as the extension so a
 * pass/fail here is diagnostic.
 *
 * The page requires no login, no extension install. Just visit it and
 * click Record.
 */
import { useEffect, useRef, useState } from "react";

const CODEC_CANDIDATES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/mp4;codecs=mp4a",
  "audio/mp4",
  "audio/ogg;codecs=opus",
];

function pickMimeType() {
  if (typeof window === "undefined" || !window.MediaRecorder) return "";
  return (
    CODEC_CANDIDATES.find((m) => MediaRecorder.isTypeSupported(m)) || ""
  );
}

export default function MicTestPage() {
  const [supported, setSupported] = useState(null);
  const [permission, setPermission] = useState("unknown");
  const [devices, setDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState("");
  const [mimeType, setMimeType] = useState("");
  const [recording, setRecording] = useState(false);
  const [level, setLevel] = useState(0);
  const [logs, setLogs] = useState([]);
  const [chunks, setChunks] = useState([]);
  const [playbackUrl, setPlaybackUrl] = useState("");
  const [transcript, setTranscript] = useState("");
  const [transcribing, setTranscribing] = useState(false);
  const [error, setError] = useState("");

  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(0);
  const chunksBufferRef = useRef([]);

  const log = (msg) => {
    const ts = new Date().toLocaleTimeString();
    setLogs((L) => [...L, `[${ts}] ${msg}`]);
    console.log("[MicTest]", msg);
  };

  // ── Boot: check API support + enumerate devices ─────────────────────
  useEffect(() => {
    const has =
      typeof navigator !== "undefined" &&
      navigator.mediaDevices &&
      typeof navigator.mediaDevices.getUserMedia === "function" &&
      typeof window.MediaRecorder !== "undefined";
    setSupported(has);
    if (!has) {
      log("MediaRecorder / getUserMedia not supported in this browser.");
      return;
    }
    const mt = pickMimeType();
    setMimeType(mt);
    log(`Supported codec: ${mt || "(browser default)"}`);

    // Attempt to check permission state (Chrome supports it; Safari doesn't).
    if (navigator.permissions?.query) {
      navigator.permissions
        .query({ name: "microphone" })
        .then((s) => {
          setPermission(s.state);
          log(`Permissions API says microphone: ${s.state}`);
          s.onchange = () => setPermission(s.state);
        })
        .catch(() => log("Permissions API not available for microphone."));
    }

    // Enumerate devices (labels are hidden until user grants once).
    navigator.mediaDevices
      .enumerateDevices()
      .then((list) => {
        const audio = list.filter((d) => d.kind === "audioinput");
        setDevices(audio);
        log(`Found ${audio.length} audio input device(s).`);
        audio.forEach((d, i) =>
          log(`  ${i + 1}. ${d.label || "(label hidden — grant permission)"} · ${d.deviceId.slice(0, 8)}…`),
        );
      })
      .catch((e) => log(`enumerateDevices failed: ${e.message}`));

    return () => {
      cancelAnimationFrame(rafRef.current);
      streamRef.current?.getTracks().forEach((t) => t.stop());
      audioCtxRef.current?.close?.().catch(() => {});
    };
  }, []);

  // ── Start recording ─────────────────────────────────────────────────
  const startRecording = async () => {
    setError("");
    setTranscript("");
    setPlaybackUrl("");
    setChunks([]);
    chunksBufferRef.current = [];

    const constraints = {
      audio: selectedDevice
        ? {
            deviceId: { exact: selectedDevice },
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          }
        : {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
    };

    let stream;
    try {
      log("Requesting getUserMedia…");
      stream = await navigator.mediaDevices.getUserMedia(constraints);
    } catch (e) {
      const msg = `${e.name}: ${e.message}`;
      log(`getUserMedia failed → ${msg}`);
      setError(msg);
      return;
    }
    streamRef.current = stream;

    const tracks = stream.getAudioTracks();
    tracks.forEach((t) => {
      log(
        `Track: "${t.label}" · state=${t.readyState} · muted=${t.muted} · enabled=${t.enabled}`,
      );
    });
    if (!tracks.length || tracks[0].readyState !== "live") {
      setError("Got a stream but no live audio track.");
      stream.getTracks().forEach((t) => t.stop());
      return;
    }
    if (tracks[0].muted) {
      log("⚠ Track muted at start — may be macOS Continuity routing.");
    }

    // Refresh device labels now that we have permission.
    try {
      const list = await navigator.mediaDevices.enumerateDevices();
      setDevices(list.filter((d) => d.kind === "audioinput"));
    } catch (_) {
      /* noop */
    }

    // ── Audio-level meter via AnalyserNode ────────────────────────────
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      const ctx = new AC();
      audioCtxRef.current = ctx;
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      src.connect(analyser);
      analyserRef.current = analyser;
      const buf = new Uint8Array(analyser.frequencyBinCount);
      const tick = () => {
        analyser.getByteTimeDomainData(buf);
        // RMS around 128 midpoint
        let sum = 0;
        for (let i = 0; i < buf.length; i++) {
          const v = (buf[i] - 128) / 128;
          sum += v * v;
        }
        const rms = Math.sqrt(sum / buf.length);
        setLevel(Math.min(1, rms * 3));
        rafRef.current = requestAnimationFrame(tick);
      };
      tick();
    } catch (e) {
      log(`AudioContext failed: ${e.message} — meter disabled.`);
    }

    // ── MediaRecorder ─────────────────────────────────────────────────
    try {
      const rec = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);
      recorderRef.current = rec;
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) {
          chunksBufferRef.current.push(e.data);
          setChunks((c) => [...c, e.data.size]);
          log(`chunk ${chunksBufferRef.current.length}: ${e.data.size} bytes`);
        } else {
          log("chunk: 0 bytes (empty)");
        }
      };
      rec.onerror = (ev) => {
        const em = ev.error?.name || "unknown";
        log(`recorder error: ${em}`);
        setError(`Recorder error: ${em}`);
      };
      rec.onstop = () => {
        const all = chunksBufferRef.current;
        const total = all.reduce((n, b) => n + b.size, 0);
        log(`stopped — ${all.length} chunks · ${total} bytes total`);
        if (all.length && total > 0) {
          const blob = new Blob(all, { type: rec.mimeType || mimeType || "audio/webm" });
          setPlaybackUrl(URL.createObjectURL(blob));
        } else {
          setError(
            "Recording produced 0 bytes. If the level meter also showed no movement, macOS is blocking your mic (System Settings → Privacy & Security → Microphone → Google Chrome).",
          );
        }
        cancelAnimationFrame(rafRef.current);
        setLevel(0);
        streamRef.current?.getTracks().forEach((t) => t.stop());
        audioCtxRef.current?.close?.().catch(() => {});
        audioCtxRef.current = null;
      };
      rec.start(250);
      setRecording(true);
      log(`MediaRecorder started · mimeType=${rec.mimeType || "(default)"}`);
    } catch (e) {
      const msg = `MediaRecorder construction failed: ${e.message}`;
      log(msg);
      setError(msg);
      stream.getTracks().forEach((t) => t.stop());
    }
  };

  const stopRecording = () => {
    try {
      recorderRef.current?.state === "recording" && recorderRef.current.stop();
    } catch (e) {
      log(`stop failed: ${e.message}`);
    }
    setRecording(false);
  };

  const uploadForTranscription = async () => {
    if (!chunksBufferRef.current.length) return;
    setTranscribing(true);
    setTranscript("");
    try {
      const type = recorderRef.current?.mimeType || mimeType || "audio/webm";
      const ext = type.includes("mp4")
        ? "m4a"
        : type.includes("ogg")
        ? "ogg"
        : "webm";
      const blob = new Blob(chunksBufferRef.current, { type });
      const fd = new FormData();
      fd.append("audio", blob, `mic-test.${ext}`);
      const backend = process.env.REACT_APP_BACKEND_URL || "";
      log(`Uploading ${blob.size} bytes to ${backend}/api/voice/stt…`);
      const r = await fetch(`${backend}/api/voice/stt`, {
        method: "POST",
        body: fd,
      });
      if (!r.ok) {
        const txt = await r.text().catch(() => "");
        throw new Error(`HTTP ${r.status}: ${txt.slice(0, 200)}`);
      }
      const j = await r.json();
      setTranscript(j.text || "(no text returned)");
      log(`transcribed ${(j.text || "").length} chars`);
    } catch (e) {
      log(`upload failed: ${e.message}`);
      setError(`Upload failed: ${e.message}`);
    } finally {
      setTranscribing(false);
    }
  };

  const barPct = Math.round(level * 100);
  const total = chunks.reduce((n, s) => n + s, 0);

  return (
    <div
      data-testid="mic-test-page"
      style={{
        maxWidth: 780,
        margin: "0 auto",
        padding: "48px 24px 96px",
        fontFamily:
          "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        color: "#111",
        lineHeight: 1.5,
      }}
    >
      <h1 style={{ fontSize: 32, fontWeight: 700, margin: "0 0 8px" }}>
        Microphone Test
      </h1>
      <p style={{ color: "#555", margin: "0 0 24px" }}>
        Diagnoses whether your Mac / iPhone microphone is reaching Chrome.
        No install required. If <strong>this page can record audio</strong>{" "}
        but the extension can&apos;t, the problem is in the extension. If{" "}
        <strong>this page also fails</strong>, macOS is blocking Chrome&apos;s
        mic access.
      </p>

      {/* Environment check */}
      <div
        style={{
          background: "#f6f7f8",
          border: "1px solid #e2e5e8",
          borderRadius: 12,
          padding: 16,
          marginBottom: 16,
          fontSize: 14,
        }}
        data-testid="mic-test-env"
      >
        <div>
          <strong>Browser support:</strong>{" "}
          {supported === null ? "…" : supported ? "✓ MediaRecorder + getUserMedia available" : "✗ Missing APIs"}
        </div>
        <div>
          <strong>Mic permission:</strong>{" "}
          <span data-testid="mic-permission">{permission}</span>
        </div>
        <div>
          <strong>Codec chosen:</strong>{" "}
          <code>{mimeType || "(browser default)"}</code>
        </div>
      </div>

      {/* Device selector */}
      {devices.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <label
            htmlFor="mic-device"
            style={{ display: "block", fontSize: 13, color: "#555", marginBottom: 6 }}
          >
            Input device (leave on <em>Default</em> unless testing)
          </label>
          <select
            id="mic-device"
            data-testid="mic-device-select"
            value={selectedDevice}
            onChange={(e) => setSelectedDevice(e.target.value)}
            disabled={recording}
            style={{
              width: "100%",
              padding: "10px 12px",
              borderRadius: 8,
              border: "1px solid #d0d4d8",
              fontSize: 14,
              background: "#fff",
            }}
          >
            <option value="">Default</option>
            {devices.map((d) => (
              <option key={d.deviceId} value={d.deviceId}>
                {d.label || `Device ${d.deviceId.slice(0, 8)}…`}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Record button + level meter */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 16,
          marginBottom: 12,
        }}
      >
        {recording ? (
          <button
            type="button"
            onClick={stopRecording}
            data-testid="mic-stop-btn"
            style={{
              padding: "14px 22px",
              borderRadius: 999,
              background: "#c53030",
              color: "#fff",
              border: "none",
              fontWeight: 600,
              fontSize: 16,
              cursor: "pointer",
            }}
          >
            ◼ Stop
          </button>
        ) : (
          <button
            type="button"
            onClick={startRecording}
            data-testid="mic-start-btn"
            disabled={!supported}
            style={{
              padding: "14px 22px",
              borderRadius: 999,
              background: supported ? "#111" : "#999",
              color: "#fff",
              border: "none",
              fontWeight: 600,
              fontSize: 16,
              cursor: supported ? "pointer" : "not-allowed",
            }}
          >
            ● Record
          </button>
        )}
        <div style={{ flex: 1 }}>
          <div
            style={{
              height: 14,
              background: "#eef1f4",
              borderRadius: 999,
              overflow: "hidden",
              border: "1px solid #dfe3e8",
            }}
            aria-label="input level"
            data-testid="mic-level-bar"
          >
            <div
              style={{
                width: `${barPct}%`,
                height: "100%",
                background:
                  barPct > 60
                    ? "linear-gradient(90deg,#3ecf8e,#eab308,#ef4444)"
                    : "linear-gradient(90deg,#3ecf8e,#3ecf8e)",
                transition: "width 60ms linear",
              }}
            />
          </div>
          <div style={{ fontSize: 12, color: "#666", marginTop: 4 }}>
            live level: {barPct}%{" "}
            {recording && chunks.length > 0 && (
              <span>
                · {chunks.length} chunks · {total} bytes
              </span>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div
          data-testid="mic-error"
          style={{
            background: "#fff5f5",
            border: "1px solid #f5c2c7",
            color: "#842029",
            borderRadius: 8,
            padding: 12,
            fontSize: 14,
            marginBottom: 16,
          }}
        >
          {error}
        </div>
      )}

      {/* Playback */}
      {playbackUrl && (
        <div
          style={{
            background: "#f0fdf4",
            border: "1px solid #bbf7d0",
            padding: 16,
            borderRadius: 12,
            marginBottom: 16,
          }}
          data-testid="mic-playback"
        >
          <div style={{ fontWeight: 600, marginBottom: 8 }}>
            ✓ Captured {total.toLocaleString()} bytes across {chunks.length} chunks
          </div>
          <audio
            controls
            src={playbackUrl}
            data-testid="mic-audio"
            style={{ width: "100%", marginBottom: 12 }}
          />
          <button
            type="button"
            onClick={uploadForTranscription}
            disabled={transcribing}
            data-testid="mic-transcribe-btn"
            style={{
              padding: "10px 18px",
              borderRadius: 999,
              background: "#111",
              color: "#fff",
              border: "none",
              fontWeight: 600,
              fontSize: 14,
              cursor: transcribing ? "not-allowed" : "pointer",
              opacity: transcribing ? 0.6 : 1,
            }}
          >
            {transcribing ? "Transcribing…" : "Test /api/voice/stt round-trip"}
          </button>
          {transcript && (
            <div
              data-testid="mic-transcript"
              style={{
                marginTop: 12,
                background: "#fff",
                border: "1px solid #d1d5db",
                borderRadius: 8,
                padding: 12,
                fontSize: 15,
              }}
            >
              <strong>Whisper transcript:</strong>
              <div style={{ marginTop: 4, color: "#333" }}>{transcript}</div>
            </div>
          )}
        </div>
      )}

      {/* Log console */}
      <details style={{ marginTop: 24 }} open>
        <summary
          style={{
            cursor: "pointer",
            fontWeight: 600,
            padding: "8px 0",
            fontSize: 14,
            color: "#333",
          }}
        >
          Diagnostic log ({logs.length})
        </summary>
        <pre
          data-testid="mic-log"
          style={{
            background: "#0b1020",
            color: "#c5cbd6",
            padding: 16,
            borderRadius: 8,
            fontSize: 12,
            lineHeight: 1.5,
            maxHeight: 320,
            overflow: "auto",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {logs.length ? logs.join("\n") : "(waiting…)"}
        </pre>
      </details>

      {/* Instructions */}
      <div
        style={{
          marginTop: 32,
          padding: 16,
          background: "#fffbeb",
          border: "1px solid #fde68a",
          borderRadius: 12,
          fontSize: 14,
          color: "#5b3b00",
        }}
      >
        <strong>If nothing records on Mac:</strong>
        <ol style={{ paddingLeft: 20, margin: "8px 0 0" }}>
          <li>
            Open <em>System Settings → Privacy &amp; Security → Microphone</em>{" "}
            and make sure <strong>Google Chrome</strong> is toggled ON.
          </li>
          <li>
            If you use an iPhone as Continuity Camera, disconnect it or in{" "}
            <em>System Settings → Sound → Input</em> switch to{" "}
            <strong>MacBook Microphone</strong>.
          </li>
          <li>
            Click the mic/site icon in Chrome&apos;s address bar and allow this
            site permanently.
          </li>
          <li>Refresh this page and press Record again.</li>
        </ol>
      </div>

      {/* Extension install */}
      <div
        style={{
          marginTop: 16,
          padding: 16,
          background: "#eef2ff",
          border: "1px solid #c7d2fe",
          borderRadius: 12,
          fontSize: 14,
          color: "#1e3a8a",
        }}
        data-testid="mic-test-ext-install"
      >
        <strong>Once this page records successfully</strong>, install the
        extension:
        <ol style={{ paddingLeft: 20, margin: "8px 0 0" }}>
          <li>
            <a
              href="/hustlesuite-assistant-v0.1.4.zip"
              data-testid="mic-test-ext-download"
              style={{ color: "#1d4ed8", fontWeight: 600 }}
            >
              Download HustleSuite Assistant v0.1.4 (.zip)
            </a>{" "}
            and unzip it.
          </li>
          <li>
            Open <code>chrome://extensions</code> in Chrome.
          </li>
          <li>
            Toggle <strong>Developer mode</strong> (top-right).
          </li>
          <li>
            Click <strong>Load unpacked</strong> and pick the unzipped folder
            (the one that contains <code>manifest.json</code>).
          </li>
          <li>
            Click into any input on any page and press{" "}
            <kbd>⌘</kbd>+<kbd>⇧</kbd>+<kbd>K</kbd> (Mac) or{" "}
            <kbd>Ctrl</kbd>+<kbd>Shift</kbd>+<kbd>K</kbd> (Win/Linux).
          </li>
        </ol>
      </div>
    </div>
  );
}
